/**
 * The six-step training event wizard: the core flow of the product.
 *
 * The event is persisted from the first save, in Draft, and every later step
 * writes to that row. That is the design and not an optimisation: it is what
 * makes the wizard resumable after the browser closes, and it is why this
 * screen holds almost no state of its own. The draft on the server is the
 * state; `?event_id=` is the way back to it.
 *
 * The one exception is the step-4 learner selection, which the API stores but
 * does not hand back. See `features/wizard/selection.ts`.
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { CalendarCheck, Lock } from "lucide-react"
import * as React from "react"

import {
  Button,
  Card,
  CardContent,
  ErrorState,
  PageHeader,
  Skeleton,
} from "@/components/ui"
import {
  useEvent,
  useSaveEvent,
  useSaveLearners,
  useSubmitEvent,
  type EventIn,
  type EventOut,
} from "@/features/wizard/api"
import { SaveState, Stepper } from "@/features/wizard/chrome"
import { eventBody, newEventBody } from "@/features/wizard/costs"
import { saveError, type SaveError } from "@/features/wizard/errors"
import {
  clearSelection,
  readSelection,
  writeSelection,
  type Learner,
} from "@/features/wizard/selection"
import { StepCosts } from "@/features/wizard/step-costs"
import { StepDetails } from "@/features/wizard/step-details"
import { StepIntervention } from "@/features/wizard/step-intervention"
import { StepLearners } from "@/features/wizard/step-learners"
import { StepProvider } from "@/features/wizard/step-provider"
import { StepReview } from "@/features/wizard/step-review"
import { resumeStep, type StepNumber } from "@/features/wizard/steps"
import { roleDisplayName } from "@/lib/instance"
import { ADMINISTRATOR, COORDINATOR, useSession } from "@/lib/session"

export const Route = createFileRoute("/events/new")({
  validateSearch: (search: Record<string, unknown>): { event_id?: number } => {
    const id = Number(search.event_id)
    return Number.isInteger(id) && id > 0 ? { event_id: id } : {}
  },
  component: NewEventScreen,
})

function NewEventScreen() {
  const { canWrite } = useSession()
  // Viewers reach every screen, so this one says why it is closed rather than
  // presenting a form whose every save would come back 403.
  return canWrite ? <Wizard /> : <ReadOnlyNotice />
}

function Wizard() {
  const search = Route.useSearch()
  const navigate = useNavigate()

  // Set on creation, so the very next step writes to the new row without
  // waiting for the URL to come back round.
  const [createdId, setCreatedId] = React.useState<number | null>(null)
  const eventId = createdId ?? search.event_id

  const eventQuery = useEvent(eventId)
  const event = eventQuery.data ?? null

  const [step, setStep] = React.useState<StepNumber>(1)
  const [learners, setLearners] = React.useState<Learner[]>(() =>
    readSelection(search.event_id),
  )
  const [mirrorMissing] = React.useState(
    () => search.event_id !== undefined && readSelection(search.event_id).length === 0,
  )
  const [error, setError] = React.useState<SaveError | null>(null)
  const [savedAt, setSavedAt] = React.useState<Date | null>(null)
  const [submitted, setSubmitted] = React.useState<EventOut | null>(null)

  const saveEvent = useSaveEvent()
  const saveLearners = useSaveLearners()
  const submitEvent = useSubmitEvent()
  const saving = saveEvent.isPending || saveLearners.isPending || submitEvent.isPending

  // Resume lands on the step the draft has reached, read off the draft rather
  // than remembered, because a remembered step would not survive the browser.
  const resumed = React.useRef(search.event_id === undefined)
  React.useEffect(() => {
    if (resumed.current || !event) return
    resumed.current = true
    setStep(resumeStep(event, learners.length))
  }, [event, learners.length])

  React.useEffect(() => {
    if (eventId !== undefined) writeSelection(eventId, learners)
  }, [eventId, learners])

  function go(next: StepNumber) {
    setError(null)
    setStep(next)
  }

  function onSaved(saved: EventOut, next: StepNumber) {
    setSavedAt(new Date())
    if (typeof saved.id === "number") {
      setCreatedId(saved.id)
      if (search.event_id !== saved.id) {
        // In the URL from here on, so a reload or a closed browser resumes.
        void navigate({
          to: "/events/new",
          search: { event_id: saved.id },
          replace: true,
        })
      }
    }
    go(next)
  }

  const onFailed = (cause: unknown) => setError(saveError(cause))

  function saveDraft(body: EventIn, next: StepNumber) {
    setError(null)
    saveEvent.mutate(
      { eventId, body },
      { onSuccess: (saved) => onSaved(saved, next), onError: onFailed },
    )
  }

  function chooseIntervention(interventionId: number) {
    if (!event) {
      saveDraft(newEventBody(interventionId), 2)
      return
    }
    // Re-saving an unchanged choice would stamp the draft as modified for
    // nothing, and the audit trail is evidence rather than noise.
    if (event.intervention_id === interventionId) return go(2)
    saveDraft(eventBody(event, { intervention_id: interventionId }), 2)
  }

  function chooseProvider(providerId: number) {
    if (!event) return
    if (event.provider_id === providerId) return go(3)
    saveDraft(eventBody(event, { provider_id: providerId }), 3)
  }

  function saveLearnerSelection() {
    if (eventId === undefined) return
    setError(null)
    saveLearners.mutate(
      { eventId, employeeIds: learners.map((learner) => learner.id) },
      { onSuccess: (saved) => onSaved(saved, 5), onError: onFailed },
    )
  }

  /**
   * A write from one of step 6's focused editors.
   *
   * The same call as every step makes, with two differences that are the whole
   * point of the feature. It advances nothing, because the reviewer is already
   * where they want to be; and its failure goes back to the editor rather than
   * into this screen's `error`, because a 422 belongs on the field inside the
   * dialog the user is looking at, not on the page behind it.
   *
   * Nothing here refetches. `useSaveEvent` writes the response into the query
   * cache, which is the same row this screen renders from, so the section
   * underneath the dialog is already showing the saved values by the time the
   * dialog closes.
   */
  function saveSection(
    patch: Partial<EventIn>,
    handlers: { onSuccess: () => void; onError: (cause: unknown) => void },
  ) {
    if (!event) return
    saveEvent.mutate(
      { eventId, body: eventBody(event, patch) },
      {
        onSuccess: () => {
          setSavedAt(new Date())
          handlers.onSuccess()
        },
        onError: handlers.onError,
      },
    )
  }

  /**
   * The learner selection from step 6's editor.
   *
   * Its own call because the selection is its own endpoint, and the browser's
   * copy is only committed once the server has taken it: a selection applied
   * locally and then refused would leave the mirror claiming people the draft
   * does not hold.
   */
  function saveSectionLearners(
    chosen: Learner[],
    handlers: { onSuccess: () => void; onError: (cause: unknown) => void },
  ) {
    if (eventId === undefined) return
    saveLearners.mutate(
      { eventId, employeeIds: chosen.map((learner) => learner.id) },
      {
        onSuccess: () => {
          setSavedAt(new Date())
          setLearners(chosen)
          handlers.onSuccess()
        },
        onError: handlers.onError,
      },
    )
  }

  function submit() {
    if (eventId === undefined) return
    setError(null)
    submitEvent.mutate(eventId, {
      onSuccess: (saved) => {
        clearSelection(eventId)
        setSubmitted(saved)
      },
      onError: onFailed,
    })
  }

  if (submitted) return <Submitted event={submitted} learnerCount={learners.length} />

  return (
    <>
      <PageHeader
        title="New training event"
        description="Six steps. Training records are generated on submit, all of them or none."
        actions={<NavButton to="/events">Leave the wizard</NavButton>}
      />

      <div className="mb-6">
        <SaveState
          saving={saving}
          savedAt={savedAt}
          eventNumber={event?.event_number || null}
        />
      </div>

      <Stepper current={step} onJump={go} />

      <Card>
        <CardContent className="p-4 sm:p-6">
          {eventId !== undefined && eventQuery.isPending ? (
            <DraftSkeleton />
          ) : eventQuery.isError ? (
            <ErrorState
              title="That draft could not be opened"
              description="It may have been submitted or deleted by someone else."
              onRetry={() => void eventQuery.refetch()}
            />
          ) : event && event.status !== "draft" ? (
            <AlreadySubmitted event={event} />
          ) : step === 1 || !event ? (
            <StepIntervention
              interventionId={event?.intervention_id ?? null}
              saving={saving}
              error={error}
              onNext={chooseIntervention}
            />
          ) : step === 2 ? (
            <StepProvider
              providerId={event.provider_id ?? null}
              saving={saving}
              error={error}
              onNext={chooseProvider}
              onSkip={() => go(3)}
              onBack={() => go(1)}
            />
          ) : step === 3 ? (
            <StepDetails
              event={event}
              saving={saving}
              error={error}
              onNext={(patch) => saveDraft(eventBody(event, patch), 4)}
              onBack={() => go(2)}
            />
          ) : step === 4 ? (
            <StepLearners
              learners={learners}
              onChange={setLearners}
              mirrorMissing={mirrorMissing}
              saving={saving}
              error={error}
              onNext={saveLearnerSelection}
              onBack={() => go(3)}
            />
          ) : step === 5 ? (
            <StepCosts
              event={event}
              learnerCount={learners.length}
              saving={saving}
              error={error}
              onNext={(patch) => saveDraft(eventBody(event, patch), 6)}
              onBack={() => go(4)}
            />
          ) : (
            <StepReview
              event={event}
              learners={learners}
              mirrorMissing={mirrorMissing}
              saving={saving}
              error={error}
              onSaveSection={saveSection}
              onSaveLearners={saveSectionLearners}
              onSubmit={submit}
              onBack={() => go(5)}
            />
          )}
        </CardContent>
      </Card>
    </>
  )
}

function DraftSkeleton() {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="sr-only">Opening your draft</span>
      <Skeleton className="mb-3 h-5 w-48" />
      <Skeleton className="mb-6 h-4 w-72" />
      {Array.from({ length: 5 }).map((_, row) => (
        <Skeleton key={row} className="mb-3 h-11 w-full" />
      ))}
    </div>
  )
}

function Submitted({ event, learnerCount }: { event: EventOut; learnerCount: number }) {
  // learner_count comes back from the server now that the records exist, so it
  // is the count worth trusting; the selection is only a fallback.
  const generated = event.learner_count || learnerCount
  return (
    <>
      <PageHeader title="Event submitted" />
      <Card className="max-w-xl">
        <CardContent className="p-6">
          <CalendarCheck className="mb-3 h-8 w-8 text-success" aria-hidden />
          {/* No weight utility: the base heading rule sets 700, and the CI's
              own faces carry only regular and bold, so a 600 renders as a
              different weight depending on which face a machine resolved. */}
          <h2 className="text-lg">
            {event.event_number || "The event"} is scheduled
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {generated} training record{generated === 1 ? "" : "s"} generated, one per
            learner. They start as Scheduled, and attendance is captured against them
            after the training.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <NavButton to="/events">Back to training events</NavButton>
            <NavButton to="/records" variant="secondary">
              See the records
            </NavButton>
          </div>
        </CardContent>
      </Card>
    </>
  )
}

function AlreadySubmitted({ event }: { event: EventOut }) {
  return (
    <div className="py-8 text-center">
      <h2 className="text-lg">This event has already been submitted</h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        {event.event_number || "It"} is no longer a draft, so the wizard cannot
        change it. Edit it from the training events screen instead.
      </p>
      <div className="mt-4">
        <NavButton to="/events" variant="secondary">
          Go to training events
        </NavButton>
      </div>
    </div>
  )
}

function ReadOnlyNotice() {
  return (
    <>
      <PageHeader title="New training event" />
      <Card className="max-w-xl">
        <CardContent className="p-6">
          <Lock className="mb-3 h-8 w-8 text-muted-foreground" aria-hidden />
          <h2 className="text-lg">Capturing training is not part of your role</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Creating a training event is for the {roleDisplayName(COORDINATOR)} and{" "}
            {roleDisplayName(ADMINISTRATOR)} roles. You can still read every event,
            record and report.
          </p>
          <div className="mt-6">
            <NavButton to="/events" variant="secondary">
              Go to training events
            </NavButton>
          </div>
        </CardContent>
      </Card>
    </>
  )
}

/**
 * A navigation dressed as a button. The shared vocabulary exports the button
 * but not its class recipe, and nesting a button inside a link would nest one
 * control in another, so the navigation happens on click.
 */
function NavButton({
  to,
  variant = "primary",
  children,
}: {
  to: "/events" | "/records"
  variant?: "primary" | "secondary"
  children: React.ReactNode
}) {
  const navigate = useNavigate()
  return (
    <Button variant={variant} onClick={() => void navigate({ to })}>
      {children}
    </Button>
  )
}
